import React, { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabaseClient';
import DashboardLayout from './DashboardLayout';
import styles from './EmployeeDashboard.module.css';
import { 
  FiClipboard, FiCalendar, FiClock, FiCheckCircle,
  FiAlertCircle, FiBarChart2, FiUsers, FiMessageSquare,
  FiAward, FiTrendingUp, FiArrowUp, FiArrowDown,
  FiFileText, FiBell, FiStar
} from 'react-icons/fi';

const EmployeeDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [performance, setPerformance] = useState({});
  const [feedback, setFeedback] = useState([]);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    fetchUserData();
    fetchTasks();
    fetchAttendance();
    fetchPerformance();
    fetchFeedback();
    fetchNotifications();
  }, []);

  const fetchUserData = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();
      setUser(profile);
    }
  };

  const fetchTasks = async () => {
    if (!user?.id) return;
    const { data } = await supabase
      .from('tasks')
      .select('*')
      .eq('assigned_to', user.id)
      .order('due_date', { ascending: true });
    setTasks(data || []);
  };

  const fetchAttendance = async () => {
    setAttendance({
      present: 18,
      absent: 1,
      late: 2,
      leave: 0,
      total: 21,
      percentage: 85.7
    });
  };

  const fetchPerformance = async () => {
    setPerformance({
      tasksCompleted: 24,
      tasksOverdue: 2,
      productivityScore: 85,
      peerRating: 4.2,
      managerRating: 4.5,
      achievements: ['Completed AI Certification', 'Led Sprint Successfully']
    });
  };

  const fetchFeedback = async () => {
    setFeedback([
      { from: 'John Doe', role: 'Manager', message: 'Great work on the recent project!', rating: 5, date: 'Oct 5, 2024' },
      { from: 'Jane Smith', role: 'Peer', message: 'Excellent collaboration skills', rating: 4, date: 'Oct 3, 2024' },
      { from: 'Mike Johnson', role: 'Lead', message: 'Consistent performer', rating: 5, date: 'Sep 28, 2024' }
    ]);
  };

  const fetchNotifications = async () => {
    setNotifications([
      { id: 1, message: 'Team meeting at 3 PM', type: 'meeting', read: false },
      { id: 2, message: 'Project deadline extended', type: 'task', read: false }
    ]);
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      await supabase
        .from('tasks')
        .update({ status: newStatus, updated_at: new Date() })
        .eq('id', taskId);
      fetchTasks();
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  if (loading) {
    return (
      <DashboardLayout role="Employee">
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="Employee">
      <div className={styles.employeeDashboard}>
        <div className={styles.header}>
          <h1>Welcome back, {user?.name || 'Employee'}!</h1>
          <p>Here's what's happening with your work today</p>
        </div>

        {/* Quick Stats */}
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <div className={styles.statIcon} style={{ background: '#1a73e8' }}>
              <FiClipboard />
            </div>
            <div className={styles.statInfo}>
              <h3>Tasks Completed</h3>
              <p className={styles.statValue}>{performance.tasksCompleted}</p>
              <span className={styles.positive}>+8 this month</span>
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statIcon} style={{ background: '#0f9d58' }}>
              <FiCalendar />
            </div>
            <div className={styles.statInfo}>
              <h3>Attendance</h3>
              <p className={styles.statValue}>{attendance.present}/{attendance.total}</p>
              <span>{attendance.percentage}% present</span>
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statIcon} style={{ background: '#e37400' }}>
              <FiBarChart2 />
            </div>
            <div className={styles.statInfo}>
              <h3>Productivity</h3>
              <p className={styles.statValue}>{performance.productivityScore}%</p>
              <span className={styles.positive}>Above average</span>
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statIcon} style={{ background: '#8b5cf6' }}>
              <FiAward />
            </div>
            <div className={styles.statInfo}>
              <h3>Avg Rating</h3>
              <p className={styles.statValue}>{performance.peerRating}/5</p>
              <span className={styles.positive}>Excellent</span>
            </div>
          </div>
        </div>

        {/* My Tasks */}
        <div className={styles.twoColumnGrid}>
          <div className={styles.sectionCard}>
            <h2><FiClipboard /> My Tasks</h2>
            <div className={styles.tasksList}>
              {tasks.length === 0 ? (
                <p className={styles.noData}>No pending tasks</p>
              ) : (
                tasks.map(task => (
                  <div key={task.id} className={styles.taskItem}>
                    <div className={styles.taskHeader}>
                      <div>
                        <h4>{task.title}</h4>
                        <p>{task.description}</p>
                      </div>
                      <select 
                        value={task.status} 
                        onChange={(e) => updateTaskStatus(task.id, e.target.value)}
                        className={styles.statusSelect}
                      >
                        <option value="pending">Pending</option>
                        <option value="in-progress">In Progress</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>
                    <div className={styles.taskMeta}>
                      <span><FiClock /> Due: {new Date(task.due_date).toLocaleDateString()}</span>
                      <span className={`${styles.priorityBadge} ${styles[task.priority]}`}>
                        {task.priority}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className={styles.sectionCard}>
            <h2><FiBell /> Notifications</h2>
            <div className={styles.notificationsList}>
              {notifications.map(notif => (
                <div key={notif.id} className={styles.notificationItem}>
                  <div className={styles.notifIcon}>
                    {notif.type === 'meeting' ? <FiUsers /> : <FiClipboard />}
                  </div>
                  <div className={styles.notifContent}>
                    <p>{notif.message}</p>
                    <small>Just now</small>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Attendance Summary */}
        <div className={styles.sectionCard}>
          <h2><FiCalendar /> Attendance Summary (This Month)</h2>
          <div className={styles.attendanceGrid}>
            <div className={styles.attendanceItem}>
              <div className={styles.attendanceValue}>{attendance.present}</div>
              <div className={styles.attendanceLabel}>Present Days</div>
            </div>
            <div className={styles.attendanceItem}>
              <div className={styles.attendanceValue}>{attendance.absent}</div>
              <div className={styles.attendanceLabel}>Absent Days</div>
            </div>
            <div className={styles.attendanceItem}>
              <div className={styles.attendanceValue}>{attendance.late}</div>
              <div className={styles.attendanceLabel}>Late Arrivals</div>
            </div>
            <div className={styles.attendanceItem}>
              <div className={styles.attendanceValue}>{attendance.leave}</div>
              <div className={styles.attendanceLabel}>Leave Days</div>
            </div>
          </div>
        </div>

        {/* Recent Feedback */}
        <div className={styles.sectionCard}>
          <h2><FiMessageSquare /> Recent Feedback</h2>
          <div className={styles.feedbackList}>
            {feedback.map((item, index) => (
              <div key={index} className={styles.feedbackItem}>
                <div className={styles.feedbackHeader}>
                  <div>
                    <strong>{item.from}</strong>
                    <span className={styles.feedbackRole}>{item.role}</span>
                  </div>
                  <div className={styles.feedbackRating}>
                    {[...Array(5)].map((_, i) => (
                      <FiStar key={i} className={i < item.rating ? styles.starFilled : styles.starEmpty} />
                    ))}
                  </div>
                </div>
                <p>{item.message}</p>
                <small>{item.date}</small>
              </div>
            ))}
          </div>
        </div>

        {/* Achievements */}
        <div className={styles.sectionCard}>
          <h2><FiAward /> Recent Achievements</h2>
          <div className={styles.achievementsList}>
            {performance.achievements.map((achievement, index) => (
              <div key={index} className={styles.achievementItem}>
                <FiCheckCircle className={styles.achievementIcon} />
                <span>{achievement}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default EmployeeDashboard;